import type { PhotoFocus, PhotoRect } from './monsterPhotoSheets'

/**
 * Paquete de fotos de NPC "VANGUARD_NPC" (3 láminas JPG de 1536 × 1536, cuadrícula 4 × 4, celdas de 384 px).
 * Cada foto se asocia al ID EXACTO de 06_monsters impreso bajo ella en la lámina ("NPC 251 Hanzo the Blacksmith").
 *
 * - VANGUARD_NPC_INDICE.json no llegó adjunto (el mensaje apuntaba a una ruta temporal de tu equipo):
 *   los rectángulos se midieron sobre las láminas recibidas y la procedencia (Fanz / Webzen) y el retoque
 *   con IA quedan pendientes por foto. Cuando llegue el JSON, sus rect y classification_overrides sustituyen esto.
 * - Las fotos del staff tienen prioridad: este paquete solo completa NPC sin imagen (ver utils/entityImages).
 * - El rectángulo [x, y, ancho, alto] es relativo a la lámina de 1536 px y excluye el rótulo impreso.
 * - Una foto identifica al personaje; no confirma coordenadas, inventarios, precios ni que esté activo en Vanguard.
 * - Estos IDs se clasifican como NPC en toda la guía (la lámina los rotula como NPC).
 */
export const NPC_SHEET_SIZE = 1536
const CELL = 384
const INSET = 4

export const npcSheets: Record<number, string> = {
  1: 'https://cdn.magicpatterns.com/uploads/4JJbwKDyzVRxRm4SW6FivK/VANGUARD_NPC_01.jpg',
  2: 'https://cdn.magicpatterns.com/uploads/vKDXrH9G3XYfUsA5JVHEDD/VANGUARD_NPC_02.jpg',
  3: 'https://cdn.magicpatterns.com/uploads/gDuCjwSNpNQa9ecF4GvqGu/VANGUARD_NPC_03.jpg',
}

/**
 * Forma de la foto dentro de su celda (medida sobre las láminas):
 * sq = cuadrada 322 px · tall = 364 × 312 · land = apaisada 364 × 208 · mid = 364 × 272 · narrow = 301 × 322.
 */
type Shape = 'sq' | 'tall' | 'land' | 'mid' | 'narrow'
const SHAPES: Record<Shape, PhotoRect> = {
  sq: [31, 8, 322, 322],
  tall: [10, 13, 364, 312],
  land: [10, 65, 364, 208],
  mid: [10, 32, 364, 272],
  narrow: [41, 8, 301, 322],
}

type Cell = [id: number, name: string, shape: Shape, focus?: PhotoFocus]

/** Por lámina, fila a fila (4 por fila), en el orden impreso. */
const layout: Record<number, Cell[]> = {
  1: [
    [224, 'Guardsman', 'sq'],
    [230, 'Wandering Merchant Alex', 'tall', { fx: 0.42, fy: 0.55, zoom: 1.1 }],
    [233, 'Messenger of Archangel', 'land'],
    [237, 'Charon', 'tall', { fx: 0.4, fy: 0.5, zoom: 1 }],
    [239, 'Arena Guard', 'tall', { fx: 0.5, fy: 0.55, zoom: 1.05 }],
    [240, 'Safety Guardian', 'land', { fx: 0.62, fy: 0.55, zoom: 1 }],
    [241, 'Royal Guard Captain Lorence', 'land'],
    [242, 'Elf Lala', 'sq'],
    [243, 'Eo the Craftsman', 'tall', { fx: 0.5, fy: 0.62, zoom: 1.1 }],
    [244, 'Caren the Barmaid', 'tall', { fx: 0.48, fy: 0.5, zoom: 1.2 }],
    [245, 'Wizard Izabel', 'tall', { fx: 0.48, fy: 0.45, zoom: 1.1 }],
    [246, 'Weapons Merchant Zienna', 'tall', { fx: 0.48, fy: 0.55, zoom: 1.15 }],
    [248, 'Wandering Merchant Martin', 'land', { fx: 0.5, fy: 0.6, zoom: 1.4 }],
    [250, 'Wandering Merchant', 'tall', { fx: 0.5, fy: 0.55, zoom: 1 }],
    [251, 'Hanzo the Blacksmith', 'sq', { fx: 0.48, fy: 0.55, zoom: 1.2 }],
    [253, 'Potion Girl Amy', 'sq', { fx: 0.5, fy: 0.55, zoom: 1.15 }],
  ],
  2: [
    [254, 'Pasi the Mage', 'sq', { fx: 0.5, fy: 0.5, zoom: 1.1 }],
    [255, 'Lumen the Barmaid', 'tall', { fx: 0.48, fy: 0.45, zoom: 1.15 }],
    [256, 'Lahap', 'land', { fx: 0.7, fy: 0.5, zoom: 1 }],
    [257, 'Shadow Phantom Soldier', 'land', { fx: 0.5, fy: 0.5, zoom: 1 }],
    [259, 'Oracle Layla', 'land', { fx: 0.45, fy: 0.55, zoom: 1.1 }],
    [368, 'Elpis', 'land'],
    [375, 'Chaos Card Master', 'land'],
    [376, 'Pamela the Supplier', 'land', { fx: 0.55, fy: 0.55, zoom: 1.3 }],
    [377, 'Angela the Supplier', 'tall', { fx: 0.5, fy: 0.5, zoom: 1.1 }],
    [379, 'Natasha the Firecracker Merchant', 'land'],
    [415, 'Silvia', 'land', { fx: 0.48, fy: 0.55, zoom: 1.2 }],
    [416, 'Rhea', 'tall', { fx: 0.55, fy: 0.5, zoom: 1.1 }],
    [417, 'Marce', 'mid', { fx: 0.5, fy: 0.5, zoom: 1.05 }],
    [452, 'Seed Master', 'land', { fx: 0.42, fy: 0.5, zoom: 1 }],
    [453, 'Seed Researcher', 'tall', { fx: 0.5, fy: 0.5, zoom: 1 }],
    [479, 'Doorkeeper Titus', 'land', { fx: 0.6, fy: 0.5, zoom: 1 }],
  ],
  3: [
    [492, 'Moss', 'tall', { fx: 0.5, fy: 0.5, zoom: 1.05 }],
    [522, 'Jerint the Assistant', 'land'],
    [543, 'Gens Duprian Steward', 'land'],
    [544, 'Gens Vanert Steward', 'land'],
    [566, 'Mercenary Guild Manager Tercia', 'land', { fx: 0.55, fy: 0.45, zoom: 1.1 }],
    [567, 'Priestess Veina', 'narrow'],
    [568, 'Wandering Merchant Zyro', 'land', { fx: 0.52, fy: 0.55, zoom: 1 }],
    [577, 'Leina the General Goods Merchant', 'land', { fx: 0.55, fy: 0.6, zoom: 1.2 }],
    [578, 'Weapons Merchant Bolo', 'tall', { fx: 0.5, fy: 0.55, zoom: 1.05 }],
    [579, 'David', 'land', { fx: 0.45, fy: 0.5, zoom: 1 }],
  ],
}

export type NpcPhoto = { id: number; sheet: number; rect: PhotoRect; sourceName: string; focus: PhotoFocus }

const DEFAULT_FOCUS: PhotoFocus = { fx: 0.5, fy: 0.5, zoom: 1 }

export const npcPhotos: NpcPhoto[] = Object.entries(layout).flatMap(([sheet, list]) =>
  list.map(([id, sourceName, shape, focus], i) => {
    const [sx, sy, sw, sh] = SHAPES[shape]
    const x = (i % 4) * CELL + sx + INSET
    const y = Math.floor(i / 4) * CELL + sy + INSET
    return { id, sheet: Number(sheet), rect: [x, y, sw - INSET * 2, sh - INSET * 2] as PhotoRect, sourceName, focus: focus ?? DEFAULT_FOCUS }
  }),
)

/** IDs rotulados como NPC en las láminas: se clasifican como NPC en toda la guía (override de clasificación). */
export const npcSheetIds = new Set(npcPhotos.map((p) => p.id))

/** El recorte cae dentro de la lámina y de su propia celda, por encima del rótulo. */
export function npcRectInBounds(p: NpcPhoto): boolean {
  const [x, y, w, h] = p.rect
  if (x < 0 || y < 0 || w <= 0 || h <= 0 || x + w > NPC_SHEET_SIZE || y + h > NPC_SHEET_SIZE) return false
  const cellX = Math.floor((x + w / 2) / CELL) * CELL
  const cellY = Math.floor((y + h / 2) / CELL) * CELL
  return x >= cellX && x + w <= cellX + CELL && y >= cellY && y + h <= cellY + 334
}
